import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert, { alertClasses } from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import PropTypes from 'prop-types';

import { useUserRolePermissions } from 'hooks';
import { useGetRestrictedLocationsQuery } from '../../batchesEndpoints';

const RestrictedLocations = ({ inventoryOwner, locationId, originId, productId }) => {
  const { inventoryOwnerIdName } = inventoryOwner;
  const { id: locationIdName } = locationId;
  const { currentAffiliate } = useUserRolePermissions();

  const restrictedLocationsParams = {
    inventoryOwner: {
      companyCustomerIdName: inventoryOwnerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    locationId: locationIdName,
    originId,
    productId,
  };

  const { data: restrictedLocationsQuery = [], isFetching } = useGetRestrictedLocationsQuery({
    params: restrictedLocationsParams,
  });

  return (
    <Stack spacing={2}>
      <Alert
        icon={<InfoIcon fontSize="inherit" />}
        severity="info"
        sx={{
          [`& .${alertClasses.icon}`]: {
            color: 'graphic.main',
          },
          alignItems: 'center',
          backgroundColor: 'success.lighter',
          boxShadow: 'none',
          color: 'text.secondary',
          display: 'flex',
          typography: 'body2',
        }}
      >
        Reconsignment Control is a fee based service. Please review your data Services Agreement for
        questions on fees.
      </Alert>
      {isFetching ? (
        <CircularProgress size={48} />
      ) : (
        <Accordion sx={{ '&.Mui-expanded': { my: 1 }, boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
            sx={{ padding: 0 }}
          >
            <Stack spacing={1}>
              <Typography variant="body1bold">Restricted Locations List</Typography>
              <Typography variant="body2">{restrictedLocationsQuery.length} Locations</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ maxHeight: '164px', overflow: 'auto', padding: 0.5 }}>
            {restrictedLocationsQuery.length ? (
              restrictedLocationsQuery.map(({ locationName, locationId: id }) => (
                <Stack key={id}>
                  <Typography sx={{ fontWeight: 400, lineHeight: '16px' }} variant="caption">
                    {locationName}
                  </Typography>
                </Stack>
              ))
            ) : (
              <Typography sx={{ fontWeight: 400, lineHeight: '16px' }} variant="caption">
                There are no restricted locations.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};

RestrictedLocations.propTypes = {
  inventoryOwner: PropTypes.shape({
    inventoryOwnerIdName: PropTypes.string.isRequired,
  }).isRequired,
  locationId: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  originId: PropTypes.number.isRequired,
  productId: PropTypes.number.isRequired,
};

export default RestrictedLocations;
